import { useEffect, useState } from "react";
import { ExternalLink, Quote, Star } from "lucide-react";
import api from "@/lib/api";

const GOOGLE_ICON = "https://cdn.simpleicons.org/google";

const initials = (name = "?") => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function Stars({ n = 5, testid }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`} data-testid={testid}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < n ? "fill-[#FFC72C] text-[#FFC72C]" : "fill-slate-200 text-slate-200"}`} />
      ))}
    </div>
  );
}

function ReviewerAvatar({ item, testid }) {
  const [error, setError] = useState(false);
  if (!item.photo || error) {
    return <span className="w-14 h-14 rounded-full bg-[#17263B] text-[#FFC72C] flex items-center justify-center font-extrabold shrink-0" data-testid={testid}>{initials(item.name)}</span>;
  }
  return <img src={item.photo} alt={item.name} onError={() => setError(true)} loading="lazy" className="w-14 h-14 rounded-full object-cover shrink-0" data-testid={testid} />;
}

export default function GoogleReviews({ headingHtml, introHtml, moreLink }) {
  const [section, setSection] = useState(null);

  useEffect(() => {
    api.get("/website/sections/google_reviews").then((r) => setSection(r.data)).catch(() => setSection({ items: [] }));
  }, []);

  if (!section) return null;
  const items = (section.items || []).filter((r) => r.visible !== false && r.name && r.text)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const sourceUrl = section.settings?.source_url || moreLink?.href;
  const rating = section.settings?.rating || "4.5/5";

  return (
    <section className="bg-[#FFF8D8]/70 border-y border-[#F4E5A1]" data-testid="google-reviews">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18">
        <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-end">
          <div>
            <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-amber-700">
              <span className="w-7 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" /> Google Reviews
            </span>
            {headingHtml ? (
              <h2 className="m-0 mt-3 text-3xl sm:text-4xl lg:text-[2.8rem] font-extrabold tracking-tight text-[#17263B]" dangerouslySetInnerHTML={{ __html: headingHtml }} data-testid="google-reviews-heading" />
            ) : (
              <h2 className="m-0 mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]" data-testid="google-reviews-heading">What our Alumni are Saying on Google?</h2>
            )}
            {introHtml && <p className="m-0 mt-3 text-[15px] leading-relaxed text-slate-600 max-w-2xl" dangerouslySetInnerHTML={{ __html: introHtml }} data-testid="google-reviews-intro" />}
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-5 shadow-[0_18px_45px_-28px_rgba(23,38,59,0.3)]" data-testid="google-reviews-summary">
            <div className="flex items-center gap-3">
              <img src={GOOGLE_ICON} alt="Google" className="w-11 h-11" loading="lazy" />
              <div>
                <p className="m-0 text-sm font-extrabold text-slate-900">Google Reviews</p>
                <p className="m-0 text-2xl font-extrabold text-[#17263B]" data-testid="google-reviews-rating">{rating}</p>
              </div>
            </div>
            <Stars n={Math.round(parseFloat(rating)) || 5} testid="google-reviews-stars" />
            {sourceUrl && (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-extrabold text-amber-700 hover:text-amber-800" data-testid="google-reviews-source-link">
                {moreLink?.text || "Read More!"} <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        {items.length > 0 ? (
          <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="google-reviews-masonry">
            {items.map((r, i) => (
              <article key={`${r.name}-${i}`} className="break-inside-avoid mb-5 rounded-3xl border border-white/80 bg-white p-5 sm:p-6 shadow-[0_14px_38px_-26px_rgba(23,38,59,0.35)] hover:-translate-y-1 hover:shadow-xl transition-all" data-testid={`google-review-card-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <ReviewerAvatar item={r} testid={`google-review-avatar-${i}`} />
                  <Quote className="w-8 h-8 text-[#FFC72C] fill-[#FFC72C]" aria-hidden="true" />
                </div>
                <p className="m-0 mt-4 text-[15px] leading-relaxed text-slate-600" data-testid={`google-review-text-${i}`}>“{r.text}”</p>
                <div className="mt-4 flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <Stars n={r.rating || 5} testid={`google-review-stars-${i}`} />
                    <p className="m-0 mt-2 text-sm font-extrabold text-[#17263B]" data-testid={`google-review-name-${i}`}>{r.name}</p>
                    {(r.role || r.company) && <p className="m-0 mt-0.5 text-xs text-slate-500" data-testid={`google-review-role-${i}`}>{[r.role, r.company].filter(Boolean).join(", ")}</p>}
                  </div>
                  <img src={GOOGLE_ICON} alt="Google review" className="w-5 h-5 shrink-0" loading="lazy" />
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-3xl border border-dashed border-amber-300 bg-white/70 p-8 text-center text-slate-500" data-testid="google-reviews-empty">
            Approved Google reviews will appear here after they are added in Website CMS.
          </div>
        )}
      </div>
    </section>
  );
}
